import { z } from 'zod';

export const CAPABILITY_AVAILABILITY_STATES = [
  'available',
  'delegated',
  'read_only',
  'confirmation_required',
  'denied',
  'unsupported',
] as const;

export const POST_DEPLOYMENT_LIFECYCLE_STAGES = [
  'capture',
  'attribution',
  'evidence',
  'regression_detection',
  'diagnosis',
  'remediation',
  'verification',
] as const;

export const PRINCIPAL_CLASSES = ['none', 'local_user', 'agent_api_key', 'human_oauth'] as const;

export const EXECUTION_REQUIREMENTS = ['local', 'remote', 'hybrid'] as const;

export const ADAPTER_TREATMENTS = [
  'direct',
  'delegated',
  'read_only',
  'confirmation_required',
  'denied',
  'unsupported',
] as const;

export const CAPABILITY_MATURITY_STATES = ['shipped', 'partial', 'planned'] as const;

export const CAPABILITY_REASON_CODES = [
  'capability_available',
  'capability_read_only',
  'delegation_available',
  'human_authentication_required',
  'principal_not_allowed',
  'role_required',
  'insufficient_role',
  'security_review_required',
  'local_execution_unavailable',
  'local_human_confirmation_required',
  'local_execution_required',
  'remote_service_unavailable',
  'not_implemented',
  'unsupported_by_host',
  'unknown_operation',
] as const;

export type CapabilityAvailabilityState = (typeof CAPABILITY_AVAILABILITY_STATES)[number];
export type PostDeploymentLifecycleStage = (typeof POST_DEPLOYMENT_LIFECYCLE_STAGES)[number];
export type PrincipalClass = (typeof PRINCIPAL_CLASSES)[number];
export type ExecutionRequirement = (typeof EXECUTION_REQUIREMENTS)[number];
export type AdapterTreatment = (typeof ADAPTER_TREATMENTS)[number];
export type CapabilityMaturityState = (typeof CAPABILITY_MATURITY_STATES)[number];
export type CapabilityReasonCode = (typeof CAPABILITY_REASON_CODES)[number];

const WorkspaceRoleSchema = z.enum(['OWNER', 'ADMIN', 'MEMBER']);
const PrincipalClassSchema = z.enum(PRINCIPAL_CLASSES);
const AdapterTreatmentSchema = z.enum(ADAPTER_TREATMENTS);
const CapabilityMaturityStateSchema = z.enum(CAPABILITY_MATURITY_STATES);
const BoundedIdentifierSchema = z.string().min(1).max(128);
const OperationIdSchema = z
  .string()
  .min(3)
  .max(128)
  .regex(/^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)+$/);
const ContractVersionSchema = z.string().regex(/^\d+\.\d+\.\d+$/);
const SecurityGateSchema = z.string().min(1).max(256);

const PlannedHandoffSchema = z
  .object({
    status: z.literal('planned'),
    securityGate: SecurityGateSchema,
  })
  .strict();

export const CapabilityAvailabilitySchema = z
  .object({
    operationId: OperationIdSchema,
    contractVersion: ContractVersionSchema,
    state: z.enum(CAPABILITY_AVAILABILITY_STATES),
    reasonCode: z.enum(CAPABILITY_REASON_CODES),
    nextAction: z.string().min(1).max(256),
    requiredPrincipal: PrincipalClassSchema,
    requiredRole: WorkspaceRoleSchema.optional(),
    requiredFacilities: z.array(BoundedIdentifierSchema).max(16),
    handoff: PlannedHandoffSchema.optional(),
  })
  .strict();

export type CapabilityAvailability = z.infer<typeof CapabilityAvailabilitySchema>;

export const PostDeploymentCapabilitySchema = z
  .object({
    id: OperationIdSchema,
    contractVersion: ContractVersionSchema,
    lifecycleStage: z.enum(POST_DEPLOYMENT_LIFECYCLE_STAGES),
    actors: z
      .array(z.enum(['developer', 'lead', 'administrator', 'runtime']))
      .min(1)
      .max(4),
    authorization: z
      .object({
        allowedPrincipals: z.array(PrincipalClassSchema).min(1).max(PRINCIPAL_CLASSES.length),
        allowedRoles: z.array(WorkspaceRoleSchema).max(3),
        agentPrincipal: z.enum(['allowed', 'denied', 'not_applicable']),
        workspaceScoped: z.boolean(),
      })
      .strict(),
    applicationContract: z
      .object({
        request: BoundedIdentifierSchema,
        response: BoundedIdentifierSchema,
      })
      .strict(),
    execution: z
      .object({
        requirement: z.enum(EXECUTION_REQUIREMENTS),
        handoff: z.enum(['none', 'planned_security_gated', 'not_applicable']),
      })
      .strict(),
    adapters: z
      .object({
        cli: AdapterTreatmentSchema,
        localMcp: AdapterTreatmentSchema,
        remoteMcp: AdapterTreatmentSchema,
        web: AdapterTreatmentSchema,
      })
      .strict(),
    safety: z
      .object({
        destructive: z.boolean(),
        localConfirmationRequired: z.boolean(),
        baseHashCasRequired: z.boolean(),
        configuredRootRequired: z.boolean(),
        receiptRequired: z.boolean(),
      })
      .strict(),
    maturity: z
      .object({
        contract: CapabilityMaturityStateSchema,
        application: CapabilityMaturityStateSchema,
        cli: CapabilityMaturityStateSchema,
        localMcp: CapabilityMaturityStateSchema,
        remoteMcp: CapabilityMaturityStateSchema,
        web: CapabilityMaturityStateSchema,
        securityReview: z.enum(['not_required', 'required', 'blocked', 'approved']),
      })
      .strict(),
    securityGate: SecurityGateSchema.optional(),
  })
  .strict()
  .superRefine((capability, context) => {
    const agentIsAllowed = capability.authorization.allowedPrincipals.includes('agent_api_key');
    const agentPolicyAllows = capability.authorization.agentPrincipal === 'allowed';

    if (agentIsAllowed !== agentPolicyAllows) {
      context.addIssue({
        code: 'custom',
        path: ['authorization', 'agentPrincipal'],
        message:
          'agentPrincipal must be allowed exactly when allowedPrincipals includes agent_api_key',
      });
    }

    const needsSecurityGate =
      capability.execution.handoff === 'planned_security_gated' ||
      capability.maturity.securityReview === 'blocked' ||
      capability.safety.localConfirmationRequired ||
      capability.safety.baseHashCasRequired ||
      capability.safety.configuredRootRequired ||
      capability.safety.receiptRequired;

    if (needsSecurityGate && capability.securityGate === undefined) {
      context.addIssue({
        code: 'custom',
        path: ['securityGate'],
        message: 'A securityGate is required for security-gated or local-mutation capabilities',
      });
    }
  });

export type PostDeploymentCapability = z.infer<typeof PostDeploymentCapabilitySchema>;
